import { useState } from "react";
import { Network, Globe } from "lucide-react";
import { useTheme } from "../context/ThemeContext";
import { useAirline } from "../context/AirlineContext";
import OverlayWrapper from "../shared/OverlayWrapper";
import { AirlineSlideHead } from "../shared/AirlineSlideRenderers";

const SlideSQTeam = () => {
  const { isDark } = useTheme();
  const { airline } = useAirline();
  const [showOrganigram, setShowOrganigram] = useState(false);
  const brand = airline.brandColor || "#F2A900";
  const t = airline?.content?.team || {};
  const groups = t.locationGroups || [];
  const clusters = t.clusters || [];

  return (
    <div>
      <AirlineSlideHead
        eyebrow="Team"
        title="Singapore-based,"
        accentLine="globally supported."
        airline={airline}
        accent={brand}
        action={
          <button
            onClick={() => setShowOrganigram(true)}
            data-testid="open-organigram-btn"
            className={`inline-flex items-center gap-2 h-10 px-4 rounded-full text-[13px] font-medium flex-shrink-0 ${
              isDark
                ? "bg-white/[0.06] text-white border border-white/15 hover:bg-white/[0.10]"
                : "bg-black/[0.04] text-[#1d1d1f] border border-black/15 hover:bg-black/[0.07]"
            }`}
          >
            <Network className="w-4 h-4" /> View organigram
          </button>
        }
      />
      <p className={`k-lead max-w-[54ch] mb-2 ${isDark ? "text-white/70" : "text-black/60"}`}>
        {t.subheading}
      </p>
      {t.highlightBadge && (
        <p className="text-[15px] font-semibold" style={{ color: brand }}>{t.highlightBadge}</p>
      )}

      <div className={`mt-10 pt-6 border-t grid grid-cols-3 gap-8 ${isDark ? "border-white/10" : "border-black/10"}`}>
        {groups.map((group, gi) => {
          const isOn = group.highlighted;
          return (
            <div key={gi} data-testid={`team-group-${group.location.toLowerCase()}`}>
              <div className="flex items-center gap-2 mb-4">
                <span className="text-[18px] leading-none">{group.flag}</span>
                <span
                  className="k-eyebrow"
                  style={{ color: isOn ? brand : (isDark ? "rgba(255,255,255,0.45)" : "rgba(0,0,0,0.45)") }}
                >
                  {group.location}
                </span>
                {isOn && (
                  <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full uppercase tracking-wider" style={{ backgroundColor: `${brand}25`, color: brand }}>
                    Local
                  </span>
                )}
              </div>
              <ul className={`space-y-3 ${isDark ? "text-white/80" : "text-black/75"}`}>
                {group.members.map((m, mi) => (
                  <li key={mi} className="text-[14px]">
                    <div className="flex items-center gap-1.5">
                      <span className={`font-semibold ${isDark ? "text-white" : "text-[#1d1d1f]"}`}>{m.name}</span>
                      {m.secondaryFlag && <span title={m.note || ""}>{m.secondaryFlag}</span>}
                    </div>
                    <div className={`text-[12px] ${isDark ? "text-white/50" : "text-black/50"}`}>{m.role}</div>
                    {m.note && (
                      <div className="text-[11px] italic mt-0.5" style={{ color: brand }}>{m.note}</div>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          );
        })}
      </div>

      <div className="mt-10 grid grid-cols-3 gap-4">
        {clusters.map((cluster, i) => (
          <div key={i}>
            <div className="k-eyebrow" style={{ color: brand }}>{cluster.label}</div>
            <div className="mt-3 flex flex-wrap gap-1.5">
              {cluster.tags.map((tag, j) => (
                <span
                  key={j}
                  className="text-[12px] px-2.5 py-1 rounded-full font-medium"
                  style={{
                    backgroundColor: isDark ? `${brand}25` : `${brand}18`,
                    color: isDark ? brand : "#1d1d1f",
                  }}
                >
                  {tag}
                </span>
              ))}
            </div>
          </div>
        ))}
      </div>

      {t.footNote && (
        <div className={`mt-10 pt-6 border-t flex items-center gap-2 text-[13px] italic ${
          isDark ? "border-white/10 text-white/45" : "border-black/10 text-black/45"
        }`}>
          <Globe className="w-3.5 h-3.5" />
          {t.footNote}
        </div>
      )}

      {showOrganigram && t.organigramUrl && (
        <OverlayWrapper
          isDark={isDark}
          onClose={() => setShowOrganigram(false)}
          icon={Network}
          iconColor={brand}
          title="Team Organigram"
          subtitle={`${airline.name} dedicated team structure`}
          widthClass="max-w-7xl"
        >
          <div style={{ background: "linear-gradient(to bottom, #0f172a, #020617)", padding: "3%" }}>
            <img src={t.organigramUrl} alt={`${airline.name} Team Organigram`} className="w-full h-auto object-contain block" data-testid="organigram-image" />
          </div>
        </OverlayWrapper>
      )}
    </div>
  );
};

export default SlideSQTeam;
